/**
 * CourtRow — Phase 178.6.H — Lifecycle-aware court card.
 *
 * Replaces the legacy single "X / Remove" affordance with an operator-grade
 * ⋮ menu that surfaces the full court lifecycle (Edit · Maintenance ·
 * Hidden · Archived · Restore · Delete).  State-aware: only the legal
 * transitions for the court's current status surface in the menu.
 *
 * Smart confirmation dialogs:
 *   • Archive / Hide / Maintenance / Restore — calm prose + one button
 *   • Hard delete — runs the dependency check; if any history exists,
 *     the dialog reframes itself as "Soft-delete (preserves history)"
 *     so the operator never silently loses data.
 */
import { useState, useMemo } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  BulletproofMenu, BulletproofMenuTrigger, BulletproofMenuContent,
  DropdownItem, DropdownDivider,
} from '@/components/ui/bulletproof-menu';
import {
  MoreVertical, Wrench, EyeOff, Archive, RotateCcw, Trash2, Loader2,
  AlertTriangle, History,
} from 'lucide-react';import { track } from '@/lib/analytics';

// ── Status presentation primitives ────────────────────────────────────

const STATUS_META = {
  active:      { label: 'Active',      tone: '#10b981', bg: 'rgba(16,185,129,0.10)' },
  hidden:      { label: 'Hidden',      tone: '#6366f1', bg: 'rgba(99,102,241,0.10)' },
  archived:    { label: 'Archived',    tone: '#64748b', bg: 'rgba(100,116,139,0.10)' },
  maintenance: { label: 'Maintenance', tone: '#f59e0b', bg: 'rgba(245,158,11,0.10)' },
  deleted:     { label: 'Deleted',     tone: 'hsl(0 70% 55%)', bg: 'rgba(244,63,94,0.10)' },
};

function StatusBadge({ status, onClick }) {
  const meta = STATUS_META[status] || STATUS_META.active;
  const interactive = !!onClick;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!interactive}
      className={`text-[9px] font-bold uppercase tracking-[0.18em] px-2 py-0.5 rounded-full transition-opacity ${interactive ? 'cursor-pointer hover:opacity-80' : 'cursor-default'}`}
      style={{ color: meta.tone, background: meta.bg, border: `1px solid ${meta.tone}33` }}
      data-testid={`court-status-badge-${status}`}
      title={interactive ? 'View lifecycle history' : undefined}
    >
      {meta.label}
    </button>
  );
}

// ── Lifecycle dialogs ─────────────────────────────────────────────────

function LifecycleDialog({
  open, court, action, onCancel, onConfirm, submitting,
}) {
  if (!court || !action) return null;
  const COPY = {
    hidden: {
      title: 'Hide court',
      icon: EyeOff,
      desc: `Hide "${court.name}" from public booking. The court stays in your operator view and can be revealed again any time. Useful for seasonal shutdowns or private leagues.`,
      confirmLabel: 'Hide court',
      variant: 'default',
    },
    maintenance: {
      title: 'Enter maintenance mode',
      icon: Wrench,
      desc: `Mark "${court.name}" as under maintenance. All future available slots will be cascaded to maintenance state so no player can book this court. Your operator-blocked slots remain untouched.`,
      confirmLabel: 'Enter maintenance',
      variant: 'default',
    },
    archived: {
      title: 'Archive court',
      icon: Archive,
      desc: `Archive "${court.name}" from operations. This removes it from the booking flow while preserving every historical booking, payment, and analytics record. Restore any time from the archived view.`,
      confirmLabel: 'Archive court',
      variant: 'default',
    },
    restore: {
      title: 'Restore court',
      icon: RotateCcw,
      desc: `Restore "${court.name}" back to active operations. Players will be able to book this court again immediately.`,
      confirmLabel: 'Restore',
      variant: 'default',
    },
  };
  const cfg = COPY[action];
  if (!cfg) return null;
  const Icon = cfg.icon;
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onCancel()}>
      <DialogContent className="sm:max-w-md" data-testid={`court-lifecycle-dialog-${action}`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Icon className="h-5 w-5" /> {cfg.title}
          </DialogTitle>
          <DialogDescription className="leading-relaxed pt-1">
            {cfg.desc}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onCancel} disabled={submitting}
                  data-testid={`court-lifecycle-cancel-${action}`}>
            Cancel
          </Button>
          <Button onClick={onConfirm} disabled={submitting}
                  data-testid={`court-lifecycle-confirm-${action}`}>
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Icon className="h-4 w-4" />}
            {cfg.confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DeleteDialog({ open, court, deps, depsLoading, onCancel, onConfirm, submitting }) {
  if (!court) return null;
  const hasHistory = !!deps?.has_any;
  const willHardDelete = !depsLoading && !hasHistory;
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onCancel()}>
      <DialogContent className="sm:max-w-md" data-testid="court-delete-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {willHardDelete
              ? <><Trash2 className="h-5 w-5" style={{ color: 'hsl(0 70% 55%)' }} /> Delete court permanently</>
              : <><AlertTriangle className="h-5 w-5" style={{ color: 'hsl(0 70% 55%)' }} /> Soft-delete court</>
            }
          </DialogTitle>
          <DialogDescription className="leading-relaxed pt-1">
            {depsLoading ? (
              <>Checking dependencies for <strong>{court.name}</strong>…</>
            ) : willHardDelete ? (
              <>
                <strong>{court.name}</strong> has no bookings, payments, or completed sessions.
                It can be safely removed forever.  This action cannot be undone.
              </>
            ) : (
              <>
                Historical activity exists for <strong>{court.name}</strong>.
                Court will be soft-deleted to preserve operational history — every
                booking, payment, and audit trail remains queryable for forensics.
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        {!depsLoading && hasHistory && (
          <div
            className="rounded-lg border p-3 text-xs space-y-1"
            style={{ borderColor: 'hsl(var(--border))', background: 'hsl(var(--muted) / 0.4)' }}
            data-testid="court-delete-deps-summary"
          >
            <div className="flex items-center gap-1.5 font-bold mb-1">
              <History className="h-3.5 w-3.5" /> Historical activity
            </div>
            <div>· {deps.bookings} total bookings</div>
            <div>· {deps.paid_bookings} paid / confirmed bookings</div>
            <div>· {deps.completed_slots} completed sessions</div>
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onCancel} disabled={submitting || depsLoading}
                  data-testid="court-delete-cancel">
            Cancel
          </Button>
          <Button
            onClick={onConfirm}
            disabled={submitting || depsLoading}
            style={{
              background: willHardDelete ? 'hsl(0 70% 55%)' : 'hsl(var(--primary))',
              color: 'white',
            }}
            data-testid="court-delete-confirm"
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> :
              (willHardDelete ? <Trash2 className="h-4 w-4" /> : <Archive className="h-4 w-4" />)}
            {willHardDelete ? 'Delete permanently' : 'Soft-delete'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── CourtRow ──────────────────────────────────────────────────────────

export default function CourtRow({ court, venueId, onChange, onViewHistory }) {
  const [submitting, setSubmitting] = useState(false);
  const [pendingAction, setPendingAction] = useState(null); // 'hidden'|'maintenance'|'archived'|'restore'
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deps, setDeps] = useState(null);
  const [depsLoading, setDepsLoading] = useState(false);

  const status = court.status || (court.is_active ? 'active' : 'archived');

  const menuItems = useMemo(() => {
    // Phase 178.6.H — View history is always available regardless of status
    const viewHistory = onViewHistory
      ? [{ id: 'history', label: 'View history', icon: History, onSelect: () => onViewHistory(court) }]
      : [];
    // State-aware menu per user spec
    if (status === 'active') {
      return [
        ...viewHistory,
        { id: 'maintenance', label: 'Maintenance mode', icon: Wrench, onSelect: () => setPendingAction('maintenance') },
        { id: 'hidden', label: 'Hide from public', icon: EyeOff, onSelect: () => setPendingAction('hidden') },
        { id: 'archived', label: 'Archive court', icon: Archive, onSelect: () => setPendingAction('archived') },
        { id: 'delete', label: 'Delete court', icon: Trash2, destructive: true, onSelect: () => openDelete() },
      ];
    }
    if (status === 'hidden') {
      return [
        ...viewHistory,
        { id: 'restore', label: 'Restore (make visible)', icon: RotateCcw, onSelect: () => setPendingAction('restore') },
        { id: 'archived', label: 'Archive court', icon: Archive, onSelect: () => setPendingAction('archived') },
        { id: 'delete', label: 'Delete court', icon: Trash2, destructive: true, onSelect: () => openDelete() },
      ];
    }
    if (status === 'maintenance') {
      return [
        ...viewHistory,
        { id: 'restore', label: 'Exit maintenance', icon: RotateCcw, onSelect: () => setPendingAction('restore') },
        { id: 'archived', label: 'Archive court', icon: Archive, onSelect: () => setPendingAction('archived') },
        { id: 'delete', label: 'Delete court', icon: Trash2, destructive: true, onSelect: () => openDelete() },
      ];
    }
    if (status === 'archived') {
      return [
        ...viewHistory,
        { id: 'restore', label: 'Restore court', icon: RotateCcw, onSelect: () => setPendingAction('restore') },
        { id: 'delete', label: 'Delete court', icon: Trash2, destructive: true, onSelect: () => openDelete() },
      ];
    }
    return viewHistory;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, onViewHistory]);

  const openDelete = async () => {
    setDeleteOpen(true);
    setDeps(null);
    setDepsLoading(true);
    track('court_delete_inspect', { court_id: court.id, status });
    try {
      const r = await api.get(`/arenax/host/venue/${venueId}/courts/${court.id}/dependencies`);
      setDeps(r.data.dependencies);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not inspect dependencies');
      setDeleteOpen(false);
    } finally { setDepsLoading(false); }
  };

  const runStatusChange = async (target) => {
    setSubmitting(true);
    track('court_status_change_attempt', { court_id: court.id, from: status, to: target });
    try {
      const url = target === 'restore'
        ? `/arenax/host/venue/${venueId}/courts/${court.id}/restore`
        : `/arenax/host/venue/${venueId}/courts/${court.id}/status`;
      const body = target === 'restore' ? {} : { status: target };
      const method = target === 'restore' ? 'post' : 'patch';
      const r = await api[method](url, body);
      const finalStatus = r.data.status;
      const cascade = r.data.cascade_slots_affected;
      toast.success(
        target === 'restore'
          ? (cascade > 0 ? `Restored · ${cascade} slot${cascade === 1 ? '' : 's'} reopened` : 'Restored to active')
          : (cascade > 0 ? `${finalStatus} · ${cascade} future slot${cascade === 1 ? '' : 's'} cascaded` : `Court marked ${finalStatus}`)
      );
      track('court_status_change_success', { court_id: court.id, to: finalStatus, cascade });
      onChange?.(r.data.court);
      setPendingAction(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not change court status');
      track('court_status_change_failed', { court_id: court.id, to: target });
    } finally { setSubmitting(false); }
  };

  const runDelete = async () => {
    setSubmitting(true);
    track('court_delete_attempt', { court_id: court.id, has_deps: !!deps?.has_any });
    try {
      const r = await api.delete(`/arenax/host/venue/${venueId}/courts/${court.id}`);
      toast.success(r.data?.hard ? 'Court deleted permanently' : (r.data?.message || 'Court soft-deleted'));
      track('court_delete_success', { court_id: court.id, hard: r.data?.hard });
      setDeleteOpen(false);
      onChange?.({ ...court, status: 'deleted', is_active: false, _removed: r.data?.hard });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not delete court');
      track('court_delete_failed', { court_id: court.id });
    } finally { setSubmitting(false); }
  };

  return (
    <>
      <div
        className="px-4 py-3 rounded-xl border flex items-start justify-between transition-colors"
        style={{
          borderColor: 'var(--ax-border-quiet)',
          background: 'var(--ax-card-bg-soft)',
          opacity: status === 'archived' || status === 'deleted' ? 0.7 : 1,
        }}
        data-testid={`venue-manage-court-${court.id}`}
      >
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="text-sm font-bold truncate" style={{ color: 'hsl(var(--ax-text))' }}>
              {court.name}
            </div>
            <StatusBadge status={status} onClick={onViewHistory ? () => onViewHistory(court) : undefined} />
          </div>
          <div className="text-[11px] mt-0.5" style={{ color: 'hsl(var(--ax-text-muted))' }}>
            {court.sport || '—'} {court.indoor ? '· Indoor' : ''}
          </div>
        </div>

        <BulletproofMenu>
          <BulletproofMenuTrigger asChild>
            <button
              className="h-7 w-7 flex items-center justify-center rounded-md hover:bg-black/[0.04]"
              style={{ color: 'hsl(var(--ax-text-faint))' }}
              title="Court actions"
              aria-label={`Open actions for ${court.name}`}
              data-testid={`venue-manage-court-menu-${court.id}`}
            >
              <MoreVertical className="h-3.5 w-3.5" />
            </button>
          </BulletproofMenuTrigger>
          <BulletproofMenuContent
            className="absolute right-0 mt-1 w-56 rounded-xl border bg-card shadow-xl z-50"
          >
            {menuItems.map((item, idx) => (
              <div key={item.id}>
                {idx > 0 && item.destructive && <DropdownDivider />}
                <DropdownItem
                  icon={item.icon}
                  destructive={item.destructive}
                  onSelect={item.onSelect}
                  testid={`venue-manage-court-action-${court.id}-${item.id}`}
                >
                  {item.label}
                </DropdownItem>
              </div>
            ))}
          </BulletproofMenuContent>
        </BulletproofMenu>
      </div>

      <LifecycleDialog
        open={!!pendingAction}
        court={court}
        action={pendingAction}
        submitting={submitting}
        onCancel={() => !submitting && setPendingAction(null)}
        onConfirm={() => runStatusChange(pendingAction)}
      />

      <DeleteDialog
        open={deleteOpen}
        court={court}
        deps={deps}
        depsLoading={depsLoading}
        submitting={submitting}
        onCancel={() => !submitting && setDeleteOpen(false)}
        onConfirm={runDelete}
      />
    </>
  );
}
